import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRequest } from '../../hooks/useRequest';
import { fetchSystemPrompt, updateSystemPrompt } from '../../utils/axios/api';

export function AdminGeneralPage() {
  const { t } = useTranslation();
  const loadRequest = useRequest<{ value: string }>();
  const saveRequest = useRequest<{ value: string }>();
  const [value, setValue] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    void loadRequest.fetch(() => fetchSystemPrompt()).then((data) => {
      if (!data) return;
      setValue(data.value);
    });
  }, [loadRequest]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setSaveSuccess(false);

      const updated = await saveRequest.fetch(() =>
        updateSystemPrompt({ value }),
      );
      if (!updated) {
        return;
      }
      setValue(updated.value);
      setSaveSuccess(true);
    },
    [saveRequest, value],
  );

  return (
    <Box
      component="form"
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2,
      }}
    >
      <Typography variant="subtitle1">{t('admin.general.description')}</Typography>

      {loadRequest.isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <TextField
            label={t('admin.general.systemPrompt.label')}
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
              setSaveSuccess(false);
            }}
            multiline
            minRows={10}
            disabled={saveRequest.isLoading}
            fullWidth
          />

          {saveRequest.exception ?? loadRequest.exception ? (
            <Typography variant="body2" color="error">
              {t(
                `exceptions.${saveRequest.exception ?? loadRequest.exception}`,
              )}
            </Typography>
          ) : null}

          {saveSuccess ? (
            <Typography variant="body2" color="success.main">
              {t('admin.general.systemPrompt.saved')}
            </Typography>
          ) : null}

          <Button
            type="submit"
            variant="contained"
            disabled={saveRequest.isLoading}
            sx={{ alignSelf: 'flex-start' }}
          >
            {saveRequest.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('admin.general.systemPrompt.save')
            )}
          </Button>
        </>
      )}
    </Box>
  );
}
